<template>
  <a-card class="merchant-card" hoverable>
    <!-- ============ 卡片头 ============ -->
    <div class="card-header">
      <span class="merchant-no">{{ card.merchantNo }}</span>
      <a-tag v-if="card.clearAgreementStatus === '已配置'" color="green">
        清分 {{ card.clearAgreementNo }}
      </a-tag>
<!--      <a-tag v-else>未配置清分</a-tag>-->
    </div>
    <div class="merchant-name" :title="card.merchantName">{{ card.merchantName }}</div>

    <!-- ============ 4 项核心指标 ============ -->
    <a-row :gutter="8" class="metric-row">
      <a-col :span="12">
        <div class="metric-label">订单总额</div>
        <Balance :amount="card.totalAmount || 0"/>
        <div class="metric-sub">{{ card.totalCount || 0 }} 笔</div>
      </a-col>
      <a-col :span="12">
        <div class="metric-label">服务费</div>
        <Balance :amount="card.serviceFee || 0"/>
        <div class="metric-sub">{{ card.serviceFeeCount || 0 }} 笔</div>
      </a-col>
    </a-row>
    <a-row :gutter="8" class="metric-row">
      <a-col :span="12">
        <div class="metric-label">分润</div>
        <Balance :amount="card.profitShare || 0"/>
        <div class="metric-sub">{{ card.profitShareCount || 0 }} 笔</div>
      </a-col>
      <a-col :span="12">
        <div class="metric-label">成功率</div>
        <div class="success-rate">{{ card.successRate || 0 }}%</div>
        <div class="metric-sub">最近 {{ card.lastOrderTime ? dayjs(card.lastOrderTime).format('MM-DD') : '-' }}</div>
      </a-col>
    </a-row>

    <!-- ============ 迷你趋势图 ============ -->
    <div class="mini-chart-wrapper">
      <div v-if="!card.trend?.length" class="no-trend">暂无趋势数据</div>
      <div v-else ref="miniChart" class="mini-chart"></div>
    </div>

    <!-- ============ 操作按钮 ============ -->
    <div class="card-actions">
      <a-button block icon="unordered-list" type="primary" @click="$emit('view-flow', card.merchantNo)">
        查看流水
      </a-button>
    </div>
  </a-card>
</template>

<script>
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import Balance from '@/view/components/Balance.vue';

export default {
  name: 'MerchantCard',
  components: {Balance},
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dayjs,
      miniChart: undefined,
    };
  },
  watch: {
    'card.trend': {
      handler() {
        this.$nextTick(() => {
          this.renderMiniChart();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderMiniChart();
    });
  },
  beforeDestroy() {
    if (this.miniChart) {
      this.miniChart.dispose();
      this.miniChart = undefined;
    }
  },
  methods: {
    renderMiniChart() {
      const trend = this.card.trend || [];
      if (!trend.length || !this.$refs.miniChart) return;

      const dates = trend.map((p) => p.date?.slice(5) || '');
      const amounts = trend.map((p) => Number(p.amount || 0));

      if (!this.miniChart) {
        this.miniChart = echarts.init(this.$refs.miniChart);
      }
      this.miniChart.setOption({
        grid: {top: 10, right: 10, bottom: 20, left: 30},
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {fontSize: 9},
          axisLine: {show: false},
          axisTick: {show: false},
        },
        yAxis: {
          type: 'value',
          axisLabel: {fontSize: 9, formatter: (v) => v >= 10000 ? `${(v / 10000).toFixed(1)}万` : v},
          splitLine: {lineStyle: {type: 'dashed', color: '#eee'}},
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const item = params[0];
            return `${item.axisValue}<br/>金额：${item.value} 元`;
          },
        },
        series: [{
          type: 'line',
          data: amounts,
          smooth: true,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: {color: '#1890ff', width: 2},
          itemStyle: {color: '#1890ff'},
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {offset: 0, color: 'rgba(24,144,255,0.3)'},
              {offset: 1, color: 'rgba(24,144,255,0)'},
            ]),
          },
        }],
      });
    },
  },
};
</script>

<style lang="less" scoped>
.merchant-card {
  margin-bottom: 16px;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;

    .merchant-no {
      font-weight: 600;
      color: #1890ff;
    }
  }

  .merchant-name {
    color: #262626;
    font-size: 14px;
    margin-bottom: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .metric-row {
    margin-bottom: 8px;

    .metric-label {
      font-size: 12px;
      color: #999;
      margin-bottom: 4px;
    }

    .metric-sub {
      font-size: 11px;
      color: #ccc;
      margin-top: 2px;
    }

    .success-rate {
      font-size: 18px;
      font-weight: 600;
      color: #52c41a;
    }
  }

  .mini-chart-wrapper {
    margin: 12px -16px;
    padding: 0 16px;

    .mini-chart {
      width: 100%;
      height: 100px;
    }

    .no-trend {
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ccc;
      font-size: 12px;
    }
  }

  .card-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;

    .ant-btn {
      flex: 1;
    }
  }
}
</style>